/**
 * GeoManager — API Client
 *
 * All HTTP calls to the geo-manager backend.
 */

import { config } from './state.js';

const defaultTimeout = 30000;

async function apiFetch(endpoint, options = {}, timeout = null) {
    const url = `${config.apiBase}${endpoint}`;
    const defaultOptions = {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout || defaultTimeout);

    try {
        const response = await fetch(url, {
            ...defaultOptions,
            ...options,
            signal: controller.signal
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Erreur serveur' }));
            throw new Error(error.detail || 'Erreur API');
        }

        return response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            throw new Error('Requête timeout - le serveur ne répond pas');
        }
        throw error;
    } finally {
        clearTimeout(timeoutId);
    }
}

export const api = {
    fetch: apiFetch,

    // Folders
    getFolders() { return apiFetch('/folders'); },
    createFolder(data) { return apiFetch('/folders', { method: 'POST', body: JSON.stringify(data) }); },
    updateFolder(folderId, data) { return apiFetch(`/folders/${folderId}`, { method: 'PUT', body: JSON.stringify(data) }); },
    deleteFolder(folderId) { return apiFetch(`/folders/${folderId}`, { method: 'DELETE' }); },

    // Points
    getPoints(folderId = null) {
        const query = folderId ? `?folder_id=${folderId}` : '';
        return apiFetch(`/points${query}`);
    },
    createPoint(data) { return apiFetch('/points', { method: 'POST', body: JSON.stringify(data) }); },
    createPointsBulk(points, folderId = null) {
        return apiFetch('/points/bulk', { method: 'POST', body: JSON.stringify({ points, folder_id: folderId }) });
    },
    updatePoint(pointId, data) { return apiFetch(`/points/${pointId}`, { method: 'PUT', body: JSON.stringify(data) }); },
    deletePoint(pointId) { return apiFetch(`/points/${pointId}`, { method: 'DELETE' }); },

    // Import
    importCoordinates(text, folderId = null) {
        return apiFetch('/import/coordinates', { method: 'POST', body: JSON.stringify({ text, folder_id: folderId }) });
    },
    async importKml(file, folderId = null) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder_id', folderId || '');
        const response = await fetch(`${config.apiBase}/import/kml`, {
            method: 'POST', body: formData, credentials: 'same-origin',
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Erreur upload' }));
            throw new Error(error.detail || 'Erreur import KML');
        }
        return response.json();
    },
    importGmaps(url, name, folderId = null) {
        return apiFetch('/import/gmaps', { method: 'POST', body: JSON.stringify({ url, name, folder_id: folderId }) });
    },
    importBulk(points, folderId = null) {
        return apiFetch('/import/bulk', { method: 'POST', body: JSON.stringify({ points, folder_id: folderId }) });
    },

    // Shares
    createShare(folderId, _permissions = 'view', expiresAt = null) {
        return apiFetch('/shares', { method: 'POST', body: JSON.stringify({ folder_id: folderId, permissions: 'view', expires_at: expiresAt }) });
    },
    deleteShare(shareId) { return apiFetch(`/shares/${shareId}`, { method: 'DELETE' }); },

    // Stats & Templates
    getStats() { return apiFetch('/stats'); },
    getTemplates() { return apiFetch('/templates'); },
    applyTemplate(templateId, payload = { parent_id: null }) {
        return apiFetch(`/templates/${templateId}/apply`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    // Custom Icons
    async uploadIcon(file) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${config.apiBase}/icons/upload`, {
            method: 'POST', body: formData, credentials: 'same-origin',
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Erreur upload' }));
            throw new Error(error.detail || 'Erreur upload icône');
        }
        return response.json();
    },
    getCustomIcons() { return apiFetch('/icons'); },
    deleteIcon(filename) { return apiFetch(`/icons/${filename}`, { method: 'DELETE' }); },

    // Analyses
    getPointDemographics(pointId) { return apiFetch(`/points/${pointId}/demographics`); },
    getPointCompetitors(pointId, nafCode = '47.11F') {
        return apiFetch(`/points/${pointId}/competitors?naf_code=${nafCode}`);
    },
    getCoverage(folderId = null, radiusKm = 10) {
        return apiFetch('/coverage', { method: 'POST', body: JSON.stringify({ folder_id: folderId, radius_km: radiusKm }) });
    },
    getHeatmapData(layer = 'density') { return apiFetch(`/heatmap?layer=${layer}`); },

    // Export
    exportData(folderId = null, includeZones = true, title = 'Géo Manager Export') {
        return apiFetch('/export/data', {
            method: 'POST', body: JSON.stringify({ folder_id: folderId, include_zones: includeZones, title }),
        });
    },
    getExportGeoJSON(folderId = null) {
        const query = folderId ? `?folder_id=${folderId}` : '';
        return apiFetch(`/export/geojson${query}`);
    },
    getExportKML(folderId = null) {
        const query = folderId ? `?folder_id=${folderId}` : '';
        return apiFetch(`/export/kml${query}`);
    },

    // Routes
    optimizeRoute(pointIds, startPointId = null, returnToStart = false) {
        return apiFetch('/optimize-route', {
            method: 'POST', body: JSON.stringify({ point_ids: pointIds, start_point_id: startPointId, return_to_start: returnToStart }),
        });
    },
    getAnalysisCatalog() { return apiFetch('/analysis/catalog'); },

    // Dashboard & data diagnostics
    getDashboard() { return apiFetch('/dashboard'); },
    getDiagnostics(options = {}) {
        return apiFetch('/diagnostics', {
            method: 'POST', body: JSON.stringify({
                folder_id: options.folderId || null,
                duplicate_threshold_m: options.duplicateThreshold || 100,
                coverage_threshold_pop: options.coverageThreshold || 10000,
            }),
        });
    },

    // Search & Prediction
    search(criteria = {}) { return apiFetch('/search', { method: 'POST', body: JSON.stringify(criteria) }); },
    suggestLocations(options = {}) {
        return apiFetch('/suggest-locations', {
            method: 'POST', body: JSON.stringify({
                folder_id: options.folderId || null, center_lat: options.centerLat, center_lng: options.centerLng,
                radius_km: options.radiusKm || 50, num_suggestions: options.numSuggestions || 5, sector: options.sector || 'commerce',
            }),
        });
    },

    // Folder duplication
    duplicateFolder(folderId, options = {}) {
        return apiFetch(`/folders/${folderId}/duplicate`, {
            method: 'POST', body: JSON.stringify({
                new_name: options.newName, offset_lat: options.offsetLat || 0, offset_lng: options.offsetLng || 0,
                include_zones: options.includeZones !== false, include_metadata: options.includeMetadata !== false,
            }),
        });
    },

    // Export demographics
    exportDemographics(folderId = null, radiusKm = 10, format = 'json') {
        return apiFetch('/export/demographics', {
            method: 'POST', body: JSON.stringify({ folder_id: folderId, radius_km: radiusKm, format }),
        });
    },
};
